import React, { useCallback, useEffect, useState } from 'react'
import { Row, Col, Button } from 'react-bootstrap'
import { Installment, PaymentSchedule } from '../../../models/Installment'
import { Order } from '../../../models/Order'
import { getInstallmentDetail } from '../../../services/installmentService'
import { formatToPrice } from '../../../utility/prettifynumber'
import { convertDateFormat, getGetOrdinal } from '../../../utility/stringUtils'
import styles from './ongoingInstallment.module.css'
import { IoIosCheckmark } from 'react-icons/io'
import { Link, useHistory } from 'react-router-dom'

interface OngoingInstallmentProps {
  order: Order
}

const OngoingInstallment = ({ order }: OngoingInstallmentProps) => {
  const history = useHistory()
  const [installment, setInstallment] = useState<Installment | undefined>(
    undefined
  )
  const [nextInstallment, setNextInstallment] = useState(0)
  const [hasUnconfirmedPayment, setHasUnconfirmedPayment] = useState(false)

  useEffect(() => {
    if (!order.installmentId) return

    getInstallmentDetail(order.installmentId).then(installmentDetail => {
      setInstallment(installmentDetail)

      let hasUnpaidSchedule = false
      for (
        let i = 0;
        i < (installmentDetail?.paymentSchedule.length ?? 0);
        i++
      ) {
        const schedule = installmentDetail!.paymentSchedule[i]
        if (!schedule.isPaid && !hasUnpaidSchedule) {
          setNextInstallment(i)
          hasUnpaidSchedule = true
        }

        if (schedule.isPaid && !schedule.confirmed) {
          setHasUnconfirmedPayment(true)
        }
      }

      if (!hasUnpaidSchedule) {
        setNextInstallment(-1)
      }
    })
  }, [order.installmentId])

  const isPaymentOverdue = useCallback((schedule: PaymentSchedule) => {
    const today = new Date()
    if (!schedule.isPaid && today.getTime() >= schedule.dueDate.getTime()) {
      return true
    }
    return false
  }, [])

  const handlePayClick = () => {
    history.push(`/installments/${order.installmentId}/pay/${nextInstallment}`)
  }

  return (
    <div className={`box p-3 ${styles.wrapper}`}>
      <Row noGutters>
        <Col>
          <div>Order #{order.orderNumber}</div>
          <div>{convertDateFormat(order.createdAt)}</div>
        </Col>
        <Col className="col-auto">
          <Link to={`/orders/${order.id}`} data-testid="orderLink">
            <Button variant="secondary" size="sm">
              View order
            </Button>
          </Link>
        </Col>
      </Row>

      <div className={styles.divider} />

      <div className="text-center">
        <h4>Installment Schedule</h4>
      </div>

      {installment && (
        <div data-testid="installmentSchedule">
          <table className={`${styles.scheduleTable} table`}>
            <thead>
              <tr className={styles.tableHeader}>
                <td>Number</td>
                <td>Due Date</td>
                <td>Amount</td>
              </tr>
            </thead>
            <tbody>
              {installment.paymentSchedule.map((schedule, i) => {
                return (
                  <tr
                    key={i}
                    className={
                      (i === nextInstallment ? styles.highlighted : '') +
                      (isPaymentOverdue(schedule) ? ` ${styles.overdue}` : '')
                    }
                    data-testid="scheduleRow"
                  >
                    <td>
                      <div
                        className={
                          styles.number +
                          (i === nextInstallment
                            ? ` ${styles.numberHighlighted}`
                            : schedule.isPaid && schedule.confirmed
                            ? ` ${styles.numberDone}`
                            : '')
                        }
                      >
                        {schedule.isPaid && schedule.confirmed ? (
                          <IoIosCheckmark size={26} />
                        ) : (
                          <>{i + 1}</>
                        )}
                      </div>
                    </td>
                    <td
                      className={
                        isPaymentOverdue(schedule)
                          ? styles.pastDue
                          : schedule.isPaid && !schedule.confirmed
                          ? styles.pendingConfirmation
                          : ''
                      }
                    >
                      {isPaymentOverdue(schedule) && <small>PAST DUE</small>}
                      {schedule.isPaid && !schedule.confirmed && (
                        <small>PENDING CONFIRMATION</small>
                      )}
                      <div>{convertDateFormat(schedule.dueDate, false)}</div>
                    </td>
                    <td>
                      {i < nextInstallment || nextInstallment === -1 ? (
                        <del>{formatToPrice(installment.monthlyPayment)}</del>
                      ) : (
                        <>{formatToPrice(installment.monthlyPayment)}</>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {nextInstallment >= 0 && (
            <div className="text-center mt-4">
              <Button
                data-testid="payButton"
                onClick={handlePayClick}
                disabled={hasUnconfirmedPayment}
              >
                Pay {nextInstallment + 1}
                <sup>{getGetOrdinal(nextInstallment + 1)}</sup> installment
              </Button>

              {hasUnconfirmedPayment && (
                <div className="gray-text mt-3">
                  You may pay your next installment once the pending payment has
                  been confirmed.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OngoingInstallment
